'use client';

import type { FC, ReactNode } from 'react';
import { Suspense } from 'react';
import { defineMessages, useIntl } from 'react-intl';
import Script from 'next/script';

import { ErrorBoundary } from '@packages/shared/src/ui/ErrorBoundary/ErrorBoundary';
import { useMediaType } from '@packages/shared/src/hooks/useMediaType/useMediaType';
import { useMediaTabletType } from '@packages/shared/src/hooks/useMediaTabletType/useMediaTabletType';
import { useUrl } from '@packages/shared/src/hooks/useUrl/useUrl';
import type { FragmentType } from '@packages/gql/generated/shopping';
import { unmask } from '@packages/gql/src/betterMasking';
import type { ProductListPageLayoutFragmentFragmentDoc } from '@packages/gql/generated/shopping/ProductListPageLayoutFragmentFragmentDoc';
import { DynamicYield } from '@packages/shared/src/ui/DynamicYield/DynamicYield';
import { BypassBlock } from '@packages/shared/src/ui/BypassBlock/BypassBlock';
import type { Device } from '@packages/themes/src/default';

import { useShouldAdSlotBeVisible } from '../../hooks/useShouldAdSlotBeVisible';
import { InfiniteProductGrid } from '../InfiniteProductGrid/InfiniteProductGrid.client';
import type { FilterSettingsProps } from '../FilterSettings/FilterSettings.client';
import { FilterSettings } from '../FilterSettings/FilterSettings.client';
import { DisplaySettingsDesktop } from '../DisplaySettingsDesktop/DisplaySettingsDesktop.client';
import { DisplaySettingsMobile } from '../DisplaySettingsMobile/DisplaySettingsMobile.client';
import { SearchAdServerIntegration } from '../SearchAdServerIntegration/SearchAdServerIntegration.client';
import { SearchStructuredData } from '../SearchStructuredData/SearchStructuredData';
import { useSeenProductsTracking } from './useSeenProductsTracking';
import { getPageLeaveKey } from './getPageLeaveKey';
import { PRODUCT_LIST_ID } from '../../ids';
import { bypassProductListPageLayoutMessages } from '../../bypassBlocks.messages';
import { ProductListBlurOverlay } from './ProductListBlurOverlay.client';

/* GraphQL */ `
  fragment ProductListPageLayoutFragment on SearchProductResult {
    ...FilterSettingsFragment
    ...UsePagingFragment
    ...UseDisplayModeFragment
    ...DisplaySettingsDesktopFragment
    ...DisplaySettingsMobileFragment
    ...ProductListVirtualPageFragment
    ...InfiniteProductGridFragment
    ...SearchStructuredDataFragment
    summary {
      dominantProductGroup
    }

    ... on SuccessfulSearchResult {
      recoveryStrategy {
        ... on FilterAdjustment {
          removeFilters
        }
      }
    }
  }
`;

const messages = defineMessages({
  productListLabel: {
    id: 'search.main.productList.label',
    defaultMessage: 'Produktliste',
  },
});

export type ProductListPageLayoutProps = {
  maskedData: FragmentType<typeof ProductListPageLayoutFragmentFragmentDoc>;
  topLevelCategories?: string[];
  aboveMainContentComponent: ReactNode;
  leftSidebarDesktopComponent: ReactNode;
  renderCategoryDialogContent: FilterSettingsProps['renderCategoryDialogContent'];
  belowProductsComponent: ReactNode;
  device: Device;
  referrer?: string;
  leftSidebarAriaLabel: string;
};

/**
 * Shared layout for pages displaying product lists
 *
 * Includes the actual product grid, filtering, sorting, display mode, pagination, and slots for additional content.
 */
export const ProductListPageLayout: FC<ProductListPageLayoutProps> = ({
  maskedData,
  topLevelCategories,
  aboveMainContentComponent,
  leftSidebarDesktopComponent,
  renderCategoryDialogContent,
  belowProductsComponent,
  device,
  referrer,
  leftSidebarAriaLabel,
}) => {
  const data = unmask<typeof ProductListPageLayoutFragmentFragmentDoc>(maskedData);
  const { formatMessage } = useIntl();

  const isDesktop = useMediaType();
  const isTablet = useMediaTabletType();

  const url = useUrl();

  // TODO SEARCH-3460 / SEARCH-3489 figure out how to do this without re-declaring incrementNumberOfSeenProducts, and causing a grid re-render, on pageLeaveKey change
  const pageLeaveKey = getPageLeaveKey(url);
  const { incrementNumberOfSeenProducts } = useSeenProductsTracking(pageLeaveKey);

  const {
    summary: { dominantProductGroup },
  } = data;

  const removeFilters =
    data.__typename === 'SuccessfulSearchResult' &&
    data.recoveryStrategy?.__typename === 'FilterAdjustment' &&
    data.recoveryStrategy.removeFilters;

  const majorMkz = dominantProductGroup ?? undefined;

  const isBannerAdSlotVisible = useShouldAdSlotBeVisible('banner', majorMkz, 'doubleClick');
  const isSkyscraperAdSlotVisible = useShouldAdSlotBeVisible('skyscraper', majorMkz, 'doubleClick');

  return (
    // position relative is necessary for the SearchAdServerIntegration component
    <div className="relative flex flex-col gap-1 p-1">
      <SearchStructuredData maskedData={data} />

      {aboveMainContentComponent}

      <BypassBlock buttonText={formatMessage(bypassProductListPageLayoutMessages.filter)}>
        <FilterSettings
          maskedData={data}
          showRemovedFiltersNotification={removeFilters}
          renderCategoryDialogContent={renderCategoryDialogContent}
        />
        {isDesktop || isTablet ? (
          <DisplaySettingsDesktop maskedData={data} />
        ) : (
          <DisplaySettingsMobile maskedData={data} />
        )}
      </BypassBlock>

      <hr className="opacity-15" />

      <div className="grid [grid-auto-columns:1fr] grid-flow-col gap-1">
        <nav
          aria-controls={PRODUCT_LIST_ID}
          aria-label={leftSidebarAriaLabel}
          className="col-span-1 max-md:hidden"
        >
          {leftSidebarDesktopComponent}
        </nav>

        {/* NOTE: minWidth is required to properly propagate size constraints to RecoSlider and prevent the page from blowing up */}
        {/* see also: https://github.com/philipwalton/flexbugs#1-minimum-content-sizing-of-flex-items-not-honored */}
        <div className="col-span-3 flex min-w-0 flex-col lg:col-span-4">
          {/* NOTE: placeholder for dynamic yield category slider content, right above the product list */}
          <DynamicYield dataDyId="dy_productlist_categoryslider" />

          <section
            aria-label={formatMessage(messages.productListLabel)}
            id={PRODUCT_LIST_ID}
            className="relative flex flex-col gap-2"
          >
            <ProductListBlurOverlay topLevelCategories={topLevelCategories} />

            <BypassBlock
              buttonText={formatMessage(bypassProductListPageLayoutMessages.products)}
              className="contents"
            >
              <InfiniteProductGrid
                maskedResult={data}
                key={url}
                device={device}
                referrer={referrer}
                onProductFirstBecameVisible={incrementNumberOfSeenProducts}
                majorMkz={majorMkz}
              />
            </BypassBlock>
            {belowProductsComponent}
          </section>
        </div>

        <ErrorBoundary>
          <Suspense>
            <SearchAdServerIntegration
              slotType="skyscraper"
              mountPointId="div-ad-server-mba-serp"
              majorMkz={majorMkz}
            />
          </Suspense>
        </ErrorBoundary>
      </div>

      {(isBannerAdSlotVisible || isSkyscraperAdSlotVisible) && (
        <Script src="https://securepubads.g.doubleclick.net/tag/js/gpt.js" />
      )}
    </div>
  );
};
